import { useReactiveVar } from "@apollo/client";
import { MenuDivider, MenuItem } from "@szhsin/react-menu";
import { useState } from "react";
import {
  GetMeetingNewPageQueryQuery,
  MeetingViewMeetingNodeNewPageFragmentFragment,
} from "types/graphql-schema";

import MeetingDropdownMenu from "@apps/meeting-dropdown-menu/meeting-dropdown-menu";
import MeetingContentAccessDropdown from "@apps/meeting-settings/components/content-access-dropdown";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { leftSidePanelVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import AppPopover from "@components/popover/app-popover";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { titleCase } from "@helpers/string";

import { getShortMeetingAllowVisibilityLabel } from "../helpers";
import MeetingBot from "./meeting-bot";
import ShareNotesDialog from "./share-meeting/share-meeting-notes-dialog";

const MeetingPageHeader = ({
  meetingGroup,
  meeting,
}: {
  meetingGroup: NonNullable<
    NonNullable<GetMeetingNewPageQueryQuery["meeting"]>["meetingGroup"]
  >;
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
}) => {
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();
  const [isShowingShareNotesDialog, setIsShowingShareNotesDialog] =
    useState(false);

  const handleShareNotesDialog = () => {
    setIsShowingShareNotesDialog(true);
  };

  const handleCloseInvitationDialog = () => {
    setIsShowingShareNotesDialog(false);
  };

  const isMeetingPageFullyExpanded =
    !uiPreferenceCache.leftNavigationSidebarExpanded &&
    !uiPreferenceCache.meetingSidebarExpanded;

  const handleToggleMeetingPageFullyExpanded = () => {
    saveUiPreference({
      leftNavigationSidebarExpanded: isMeetingPageFullyExpanded,
      meetingSidebarExpanded: isMeetingPageFullyExpanded,
    });
  };
  const leftSidePanel = useReactiveVar(leftSidePanelVar);
  const meetingsPanelOpen =
    leftSidePanel === "meetings" || typeof leftSidePanel === "object";
  return (
    <>
      <MeetingContentAccessDropdown meetingGroup={meetingGroup}>
        <AppPopover.Button
          as={Button}
          theme={buttonTheme.text}
          size={ButtonSize.medium}
          leftNegativeMargin
          aria-label="Meeting header permission dropdown menu button"
          disabled={!meetingGroup?.canUpdate?.permission}
          className="min-w-0 px-0 w-[24px] @3xl/layout-header:px-2 @3xl/layout-header:w-auto" // necessary for truncating text if too long
        >
          <span className="flex items-center gap-1 min-w-0 flex-1">
            {!meetingGroup.allowAdminVisibility &&
            !meetingGroup.allowOrgVisibility &&
            !meetingGroup.allowManagementTreeVisibility ? (
              <TopicflowIcon
                type="lockClosed"
                className="h-4 w-4 text-gray-400 shrink-0"
              />
            ) : (
              <TopicflowIcon
                type="lock"
                className="h-4 w-4 text-gray-400 shrink-0"
              />
            )}
            <span className="truncate flex-1 min-w-0 hidden @3xl/layout-header:flex">
              {titleCase(
                getShortMeetingAllowVisibilityLabel({
                  allowManagementTreeVisibility:
                    meetingGroup.allowManagementTreeVisibility,
                  allowAdminVisibility: meetingGroup.allowAdminVisibility,
                  allowOrgVisibility: meetingGroup.allowOrgVisibility,
                }),
              )}
            </span>
          </span>
        </AppPopover.Button>
      </MeetingContentAccessDropdown>

      <MeetingBot
        meeting={meeting}
        textClassName="hidden @3xl/layout-header:flex"
        buttonProps={{ size: ButtonSize.medium }}
      />

      <MeetingDropdownMenu
        meeting={meeting}
        meetingGroup={meetingGroup}
        size="5"
        onOpenSendNotesDialog={handleShareNotesDialog}
        buttonProps={{ size: ButtonSize.medium }}
      >
        <>
          <MenuItem
            className={"text-sm"}
            onClick={handleToggleMeetingPageFullyExpanded}
          >
            {isMeetingPageFullyExpanded
              ? "Exit Fullscreen"
              : "Enter Fullscreen"}
          </MenuItem>
          <MenuDivider />
        </>
      </MeetingDropdownMenu>

      {meetingsPanelOpen && (
        <Button
          icon={getTopicflowIcon("expandArrows")}
          size={ButtonSize.medium}
          theme={buttonTheme.iconGray}
          onClick={() => {
            leftSidePanelVar(false);
          }}
          aria-label="Hide left side meetings panel"
        />
      )}

      {isShowingShareNotesDialog && (
        <ShareNotesDialog
          meeting={meeting}
          onClose={handleCloseInvitationDialog}
          isInviteDialog={false}
        />
      )}
    </>
  );
};

export default MeetingPageHeader;
